import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { Country, countryService } from '../../services/countryService';

interface CountryState {
  countries: Country[];
  selectedCountry: Country | null;
  searchQuery: string;
  filteredCountries: Country[];
  isLoading: boolean;
  error: string | null;
  isInitialized: boolean;
}

const initialState: CountryState = {
  countries: [],
  selectedCountry: null,
  searchQuery: '',
  filteredCountries: [],
  isLoading: false,
  error: null,
  isInitialized: false,
};

// Async thunk to fetch countries from API
export const fetchCountries = createAsyncThunk(
  'country/fetchAll',
  async (_, { rejectWithValue }) => {
    try {
      const countries = await countryService.fetchAllCountries();
      return countries;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch countries');
    }
  }
);

const countrySlice = createSlice({
  name: 'country',
  initialState,
  reducers: {
    setSelectedCountry: (state, action: PayloadAction<Country>) => {
      state.selectedCountry = action.payload;
    },
    setSearchQuery: (state, action: PayloadAction<string>) => {
      state.searchQuery = action.payload;
      state.filteredCountries = action.payload 
        ? countryService.searchCountries(state.countries, action.payload)
        : state.countries;
    },
    clearSearch: (state) => {
      state.searchQuery = '';
      state.filteredCountries = state.countries;
    },
    selectCountryByDialCode: (state, action: PayloadAction<string>) => {
      const country = countryService.getCountryByDialCode(state.countries, action.payload);
      if (country) {
        state.selectedCountry = country;
      }
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCountries.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchCountries.fulfilled, (state, action: PayloadAction<Country[]>) => {
        state.isLoading = false;
        state.countries = action.payload;
        state.filteredCountries = action.payload;
        state.isInitialized = true;
        // Set default to UAE if available
        if (!state.selectedCountry && action.payload.length > 0) {
          state.selectedCountry = countryService.getCountryByDialCode(action.payload, '+971') || action.payload[0];
        }
      })
      .addCase(fetchCountries.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
        state.isInitialized = true;
      });
  },
});

export const { 
  setSelectedCountry, 
  setSearchQuery, 
  clearSearch, 
  selectCountryByDialCode 
} = countrySlice.actions;

export default countrySlice.reducer;
